import { Suspense, useRef, useState } from 'react';
import { api } from '../api.js';
import { lazyWithRefresh, dynamicImportWithRefresh } from '../lazy-refresh.js';

// Code-split the scanner: jsqr is ~130 KB and most page loads never
// open this sheet. Lazy import keeps the main bundle slim. Both
// QrScanner (camera) and decodeQrFromImageFile (still-image upload)
// live in the same module so they share the chunk.
//
// lazyWithRefresh / dynamicImportWithRefresh wrap the dynamic imports
// so a stale-shell-vs-new-build hash mismatch auto-reloads instead of
// showing the ErrorBoundary crash. Standard SPA + code-split cost
// after a deploy.
const QrScanner = lazyWithRefresh(() =>
  import('./QrScanner.jsx').then((m) => ({ default: m.QrScanner }))
);
const importQrModule = () => dynamicImportWithRefresh(() => import('./QrScanner.jsx'));

// In-app device claim from Tank Settings → Equipment → Devices.
//
// Physical-access only: we never list other people's unclaimed devices.
// The user proves they have the device in front of them by reading its
// cid — either by scanning the QR (in-app camera, primary path), or by
// typing the cid printed beneath it. No picker.

export function AddDeviceSheet({ tank, onDone, onCancel }) {
  const [mode, setMode] = useState('scan'); // scan | manual
  const [cid, setCid] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  async function claim(rawCid) {
    const trimmed = rawCid.trim();
    if (!trimmed) {
      setErr('Enter the device ID printed on the device.');
      return;
    }
    setBusy(true);
    setErr('');
    try {
      await api('/api/devices/claim', {
        method: 'POST',
        body: { cid: trimmed, tank_id: tank.id },
      });
      await onDone();
    } catch (e) {
      setErr(messageFor(e.payload?.error) || e.message || 'Could not link that device.');
      // On error, drop into manual mode with the value prefilled so the
      // user can retry without re-scanning.
      setCid(trimmed);
      setMode('manual');
      setBusy(false);
    }
  }

  // Upload a still photo of the QR (manual-mode fallback). Loads the
  // same chunk the live scanner uses, so jsqr stays code-split. Useful
  // when the user denied camera permission or already has a photo.
  async function onPickQrImage(e) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setErr('');
    setUploading(true);
    try {
      const mod = await importQrModule();
      const raw = await mod.decodeQrFromImageFile(file);
      if (!raw) {
        setErr("Couldn't find a QR code in that image. Try a closer or sharper photo.");
        return;
      }
      const decoded = mod.extractCidFromScan(raw);
      if (!decoded) {
        setErr("That QR code didn't look like a TankSensei pairing code.");
        return;
      }
      setCid(decoded);
      await claim(decoded);
    } catch (ex) {
      setErr(ex?.message || "Couldn't read that image.");
    } finally {
      setUploading(false);
    }
  }

  if (mode === 'scan') {
    return (
      <Suspense
        fallback={
          <p className="ts-hint" style={{ marginTop: 8 }}>
            Opening camera…
          </p>
        }
      >
        <QrScanner
          onDetect={(scanned) => {
            // Show the cid in the manual view briefly if claim fails, but
            // by default fire-and-forget so the scan UX is one-tap.
            setCid(scanned);
            claim(scanned);
          }}
          onCancel={onCancel}
          onUnavailable={() => setMode('manual')}
        />
      </Suspense>
    );
  }

  return (
    <div style={{ marginTop: 8 }}>
      <p className="ts-hint" style={{ marginBottom: 8 }}>
        On the display, open its pairing screen (on the 7-inch display: Settings, then Pair with
        phone) and type the ID shown under the QR.
      </p>

      {err && (
        <div role="alert" className="ts-err" style={{ marginBottom: 8 }}>
          {err}
        </div>
      )}

      <label className="ts-field">
        <span>Device ID</span>
        <input
          type="text"
          autoCapitalize="off"
          autoComplete="off"
          spellCheck={false}
          value={cid}
          onChange={(e) => setCid(e.target.value)}
          placeholder="printedreef-prod/View7-AB12"
        />
      </label>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={onPickQrImage}
      />

      <div className="ts-row-body" style={{ marginTop: 10 }}>
        <button
          type="button"
          className="ts-save"
          onClick={() => claim(cid)}
          disabled={busy || !cid.trim()}
        >
          {busy ? '…' : 'Link to this tank'}
        </button>
        <button
          type="button"
          className="ts-chip"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading || busy}
        >
          {uploading ? 'Reading…' : 'Upload QR photo'}
        </button>
        <button
          type="button"
          className="ts-chip"
          onClick={() => {
            setErr('');
            setMode('scan');
          }}
        >
          Scan QR instead
        </button>
        <button type="button" className="ts-chip" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function messageFor(code) {
  switch (code) {
    case 'device_not_seen_yet':
      return "We haven't heard from that device yet. Make sure it's powered on and connected to Wi-Fi, then try again in a minute.";
    case 'device_already_claimed':
      return 'That device is already linked to another account. Contact support if it should be yours.';
    case 'tank_not_owned':
      return 'Only the tank owner can add a display to it.';
    case 'device_cleanup_pending':
      return 'This display is still being cleared from its last account. Try again in a minute.';
    default:
      return null;
  }
}
